import React from "react";
import styled, { createGlobalStyle, css, keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";

export const GlobalStyle = createGlobalStyle`
  html, body, #mount {
    font-family: "Plus Jakarta Sans";
    margin: 0;
    padding: 0;
    height: 100%;
    background-color: #FFD395;
    color: #000;
  }

  * {
    box-sizing: border-box;
  }
`;

export const PageContainer = styled.div`
  display: flex;
  min-height: 100dvh;
  width: 100%;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const logoTextStyle = css`
  color: #FFD395;
  font-family: "Gasoek One";
  font-size: 50px;
  font-style: normal;
  font-weight: 400;
  line-height: 90%;
  cursor: pointer;
`;

const LogoWrapper = styled.div`
  ${logoTextStyle}
  ${(props) => props.largeFont ? `
    font-size: 80px;
    font-style: normal;
    font-weight: 400;
  ` : ''}
`;

const DesktopLogo = styled.div`
  display: none;
  @media (min-width: 800px) {
    display: block;
  }
`;

const MobileLogo = styled.div`
  display: block;
  @media (min-width: 800px) {
    display: none;
  }
`;

type LogoProps = {
  largeFont?: boolean;
};

export const Logo: React.FC<LogoProps> = ({ largeFont }: LogoProps) => {
  const navigate = useNavigate();
  return (
    <LogoWrapper largeFont={largeFont} onClick={() => navigate("/")}>
      <MobileLogo>call.{largeFont && <br/>}chat</MobileLogo>
      <DesktopLogo dangerouslySetInnerHTML={{ __html: "call.<br/>chat" }} />
    </LogoWrapper>
  );
};

export const Sidebar = styled.div`
  background-color: #EE3D02;
  padding: 32px;
  display: flex;
  justify-content: flex-start;
  
  @media (min-width: 800px) {
    position: sticky;
    top: 0;
    align-self: flex-start;
    width: 200px;
    height: 100dvh;
    align-items: flex-start;
  }

  @media (max-width: 799px) {
    position: sticky;
    top: 0;
    z-index: 1002;
    width: 100%;
    height: 80px;
    align-items: center;
    padding: 16px;
    justify-content: flex-start;
  }
`;

export const MainContent = styled.main`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  text-align: center;
  padding: 32px;
`;

export const Title = styled.h1`
  color: #000;
  text-align: center;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.5px;
  margin-bottom: 10px;

  @media (min-width: 768px) {
    font-size: 40px;
  }
`;

export const Subtitle = styled.p`
  color: #000;
  text-align: center;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.44px;
  margin: 0;
`;

export const Button = styled.button`
  display: flex;
  width: 100%;
  max-width: 400px;
  padding: 20px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #FD8C0B;
  border: none;
  cursor: pointer;
  transition: filter 0.2s ease-in-out, background-color 0.2s ease-in-out;
  color: #ffffff;
  font-family: "Plus Jakarta Sans";
  font-size: 16px;
  font-weight: 700;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(1.15);
    }
  }

  &:disabled {
    background-color: #aaa;
    cursor: default;
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 400px;
`;

export const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  font-size: 14px;
  width: 100%;
`;

export const Input = styled.input`
  font-family: "Plus Jakarta Sans";
  width: 100%;
  max-width: 400px;
  padding: 16px;
  border-radius: 8px;
  border: none;
  background: #ffedcc;
  color: #000;
  font-size: 16px;

  ${(props) => props.modal && `
    background: #fff;
    outline: 1px solid rgb(204, 204, 204);
  `}

  &::placeholder {
    color: #888;
  }

  &:focus,
  &:active {
    outline: 2px solid #FD8C0B;
  }

  &:disabled {
    background-color: #eee;
  }
`;

export const OTPInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

export const OTPInputBox = styled.input`
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 24px;
  border-radius: 8px;
  border: none;
  background: #ffedcc;
  color: #000;
  font-family: "Plus Jakarta Sans";

  &:focus,
  &:active {
    outline: 2px solid #FD8C0B;
  }

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const ErrorMessage = styled.p`
  font-family: "Plus Jakarta Sans";
  color: #d93025;
  font-size: 14px;
  text-align: center;
  margin: 10px 0;
  width: 100%;
  max-width: 400px;
`;

export const SuccessMessage = styled.p`
  font-family: "Plus Jakarta Sans";
  color: #2e7d32;
  font-size: 16px;
  text-align: center;
  margin-bottom: 24px;
  padding: 16px;
  border-radius: 8px;
  background-color: #e8f5e9;
  width: 100%;
  max-width: 560px;
`;

const scroll = keyframes`
  0% { transform: translateX(100vw); }
  100% { transform: translateX(-100%); }
`;

export const ScrollingAlert = styled.div`
  background-color: #FD8C0B;
  color: white;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;

  @media (min-width: 800px) {
    position: fixed;
    top: 0;
    left: 200px;
    width: calc(100% - 200px);
    z-index: 1001;
  }

  @media (max-width: 799px) {
    position: sticky;
    top: 80px;
    z-index: 1001;
  }

  p {
    display: inline-block;
    padding-left: 5em;
    animation: ${scroll} 30s linear infinite;
    margin: 0;
    padding-top: 12px;
    padding-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
`;

export const CheckboxLabel = styled.label`
  font-family: "Plus Jakarta Sans";
  font-size: 14px;

  a {
    color: ${(props) => props.hrefColor ? props.hrefColor : "#FD8C0B"};
    font-weight: 600;
    text-decoration: none;
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        text-decoration: underline;
      }
    }

    @media (hover: none) and (pointer: coarse) {
      &:active {
        text-decoration: underline;
      }
    }    
  }
`;

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${(props) => props.accentColor ? props.accentColor : "#FD8C0B"};
`;

export const BigTitle = styled.h1`
  color: #EE3D02;
  font-family: "Gasoek One";
  font-size: 80px;
  font-style: normal;
  font-weight: 400;
  line-height: 90%;
  text-align: center;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    font-size: 60px;
  }
`;

export const SubscribeIcon = ({ className, style }: { className?: string, style?: React.CSSProperties }) => (
  <svg className={className} style={style} xmlns="http://www.w3.org/2000/svg" width="19" height="15" viewBox="0 0 19 15" fill="none">
    <path d="M16.5 0H1.83333C0.815833 0 0.00916666 0.815833 0.00916666 1.83333L0 12.8333C0 13.8508 0.815833 14.6667 1.83333 14.6667H16.5C17.5175 14.6667 18.3333 13.8508 18.3333 12.8333V1.83333C18.3333 0.815833 17.5175 0 16.5 0ZM16.5 12.8333H1.83333V7.33333H16.5V12.8333ZM16.5 3.66667H1.83333V1.83333H16.5V3.66667Z" fill="white"/>
  </svg>
);

export const CancelIcon = ({ className, style }: { className?: string, style?: React.CSSProperties }) => (
  <svg className={className} style={style} xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 20 20" fill="none">
    <g clipPath="url(#clip0_276_1185_cancel)">
      <path d="M5.7 14.3C5.88333 14.4833 6.11667 14.575 6.4 14.575C6.68333 14.575 6.91667 14.4833 7.1 14.3L10 11.4L12.925 14.325C13.1083 14.5083 13.3377 14.5957 13.613 14.587C13.8877 14.579 14.1167 14.4833 14.3 14.3C14.4833 14.1167 14.575 13.8833 14.575 13.6C14.575 13.3167 14.4833 13.0833 14.3 12.9L11.4 10L14.325 7.075C14.5083 6.89167 14.5957 6.66233 14.587 6.387C14.579 6.11233 14.4833 5.88333 14.3 5.7C14.1167 5.51667 13.8833 5.425 13.6 5.425C13.3167 5.425 13.0833 5.51667 12.9 5.7L10 8.6L7.075 5.675C6.89167 5.49167 6.66267 5.404 6.388 5.412C6.11267 5.42067 5.88333 5.51667 5.7 5.7C5.51667 5.88333 5.425 6.11667 5.425 6.4C5.425 6.68333 5.51667 6.91667 5.7 7.1L8.6 10L5.675 12.925C5.49167 13.1083 5.40433 13.3373 5.413 13.612C5.421 13.8873 5.51667 14.1167 5.7 14.3ZM10 20C8.61667 20 7.31667 19.7373 6.1 19.212C4.88333 18.6873 3.825 17.975 2.925 17.075C2.025 16.175 1.31267 15.1167 0.788 13.9C0.262667 12.6833 0 11.3833 0 10C0 8.61667 0.262667 7.31667 0.788 6.1C1.31267 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.31233 6.1 0.787C7.31667 0.262333 8.61667 0 10 0C11.3833 0 12.6833 0.262333 13.9 0.787C15.1167 1.31233 16.175 2.025 17.075 2.925C17.975 3.825 18.6873 4.88333 19.212 6.1C19.7373 7.31667 20 8.61667 20 10C20 11.3833 19.7373 12.6833 19.212 13.9C18.6873 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6873 13.9 19.212C12.6833 19.7373 11.3833 20 10 20ZM10 18C12.2167 18 14.1043 17.221 15.663 15.663C17.221 14.1043 18 12.2167 18 10C18 7.78333 17.221 5.89567 15.663 4.337C14.1043 2.779 12.2167 2 10 2C7.78333 2 5.896 2.779 4.338 4.337C2.77933 5.89567 2 7.78333 2 10C2 12.2167 2.77933 14.1043 4.338 15.663C5.896 17.221 7.78333 18 10 18Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="clip0_276_1185_cancel">
        <rect width="20" height="20" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

export const SupportIcon = ({ className, style }: { className?: string, style?: React.CSSProperties }) => (
  <svg className={className} style={style} xmlns="http://www.w3.org/2000/svg" width="17" height="20" viewBox="0 0 17 20" fill="none">
    <path d="M7.875 19.7663V16.6162C3.49125 16.3888 0 12.7487 0 8.3125C0 3.7275 3.7275 0 8.3125 0C12.8975 0 16.625 3.7275 16.625 8.3125C16.625 12.6438 13.615 17.0012 9.12625 19.1625L7.875 19.7663ZM8.3125 1.75C4.69 1.75 1.75 4.69 1.75 8.3125C1.75 11.935 4.69 14.875 8.3125 14.875H9.625V16.8875C12.81 14.875 14.875 11.5675 14.875 8.3125C14.875 4.69 11.935 1.75 8.3125 1.75ZM7.4375 11.8125H9.1875V13.5625H7.4375V11.8125ZM9.1875 10.5H7.4375C7.4375 7.65625 10.0625 7.875 10.0625 6.125C10.0625 5.1625 9.275 4.375 8.3125 4.375C7.35 4.375 6.5625 5.1625 6.5625 6.125H4.8125C4.8125 4.19125 6.37875 2.625 8.3125 2.625C10.2463 2.625 11.8125 4.19125 11.8125 6.125C11.8125 8.3125 9.1875 8.53125 9.1875 10.5Z" fill="white"/>
  </svg>
);


export const AccountContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 32px;
  align-items: center;
  
  @media (min-width: 800px) {
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    gap: 64px;
  }
`;

export const AccountDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 400px;

  @media (min-width: 800px) {
    width: 300px;
    max-width: 300px;
  }
`;


export const AccountUserInfoContainer = styled.div`
  width: 100%;
  max-width: 400px;
  color: #000;
  font-family: "Plus Jakarta Sans";
  text-align: left;

  @media (min-width: 800px) {
    width: auto;
    max-width: none;
    margin-bottom: 0;
    flex-shrink: 0;
  }

  h2 {
    font-size: 32px;
    font-weight: 700;
    margin-top: 0;
    margin-bottom: 24px;
    padding-bottom: 8px;
    border-bottom: 1px solid #000;
    display: inline-block;
  }
`;

export const UserInfoItemContainer = styled.div`
  margin-bottom: 24px;
`;

export const UserInfoLabel = styled.p`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #000;
`;

export const UserInfoValue = styled.p`
  font-size: 20px;
  margin: 0;
  color: #333;
`;

export const UserInfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <UserInfoItemContainer>
    <UserInfoLabel>{label}</UserInfoLabel>
    <UserInfoValue>{value}</UserInfoValue>
  </UserInfoItemContainer>
);

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 16px;
`;

export const ModalContent = styled.div`
  background-color: #fff;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 560px;
  color: #000;
  font-family: "Plus Jakarta Sans";
`;

export const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 24px;
`;

export const ModalLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #555;
  font-family: "Plus Jakarta Sans";
  font-size: 14px;
  text-align: left;
`;

export const ModalTextArea = styled.textarea`
  font-family: "Plus Jakarta Sans";
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #ccc;
  background: #fff;
  color: #000;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  margin-bottom: 16px;

  &::placeholder {
    color: #888;
  }

  &:focus,
  &:active {
    outline: 2px solid #FD8C0B;
    border-color: transparent;
  }
`;

export const ModalButton = styled(Button)`
  width: auto;
  padding: 12px 24px;
  font-size: 14px;
  cursor: pointer;
`;

export const CloseButton = styled(ModalButton)`
  background: #e0e0e0;
  color: #333;
  cursor: pointer;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: #d5d5d5;
      filter: none;
    }
  }

  @media (hover: none) and (pointer: coarse) {
    &:active {
      background: #d5d5d5;
      filter: none;
    }
  }   
`;
